import { Component, h } from 'preact'
import { isInputEl } from '../../libs/preact-entry'
import Icon from '../assets'
import algoliasearch from 'algoliasearch/lite'
import AutoSuggestTags from './AutoSuggestTags'
import getWithInMemoryCache from '../../utils/getWithInMemoryCache'
import { SEARCH_TOKEN_URL } from '../../libs/config'
import globals from '../../utils/globals'
import { common } from '../common'

interface Tag {
  id?: number,
  name: string
}

type TagList = Array<Tag>

interface Props {
  isEdit: boolean,
  inputName: string,
  inputId?: string,
  selectedPublicTags?: string,
  canEndUserAddPublicTags: boolean,
  onChange?: (i: Array<string>) => void,
  iconSize?: number
}
interface FacetHit {
  value: string,
  highlighted: string,
  count: number,
}
interface State {
  inputInFocus: boolean,
  publicTagList: FacetHit[],
  selectedPublicTags: TagList,
  selectedTagsSet: Set<string>,
  searchText: string,
  index?: algoliasearch.Index
}

export default class PublicTagSelect extends Component<Props, State> {
  tagInput: HTMLInputElement | undefined
  dropDown: HTMLDivElement | undefined;

  constructor (props: Props) {
    super(props)

    const selectedTagNames: TagList = props.selectedPublicTags?.length ? props.selectedPublicTags.split(',').map(t => ({ name: t })) : []
    const selectedTagsSet = new Set(selectedTagNames.map(t => t.name.toLowerCase()))

    this.state = {
      inputInFocus: false,
      publicTagList: [],
      selectedPublicTags: selectedTagNames,
      selectedTagsSet: selectedTagsSet,
      searchText: ''
    }

    this.selectPublicTag = this.selectPublicTag.bind(this)
    this.removePublicTag = this.removePublicTag.bind(this)
    this.updateSearchText = this.updateSearchText.bind(this)
    this.refreshSuggestions = this.refreshSuggestions.bind(this)
    this.isTagSelected = this.isTagSelected.bind(this)
  }

  async componentDidMount () {
    const data: {client_id: string, token: string} = await getWithInMemoryCache(SEARCH_TOKEN_URL, {})
    const client = algoliasearch(data.client_id, data.token)
    this.setState({ index: client.initIndex(globals('communityId')) })
    this.state.index.searchForFacetValues({ facetName: 'tags', facetQuery: '' }).then(({ facetHits }: { facetHits: FacetHit[] }) => {
      this.setState({ publicTagList: facetHits })
    })
  }

  componentDidUpdate (previousProps: Readonly<Props>, previousState: Readonly<State>, previousContext: any): void {
    if (this.state.inputInFocus && previousState.inputInFocus !== this.state.inputInFocus) {
      this.dropDown && 'scrollIntoView' in this.dropDown && this.dropDown.scrollIntoView({ block: 'end', behavior: 'smooth' })
    }
  }

  updateSearchText (e: KeyboardEvent) {
    if (e.code === 'Escape') {
      this.tagInput && this.tagInput.blur()
      return
    }

    const target = e?.target
    if (!isInputEl(target)) {
      return
    }

    const value = target.value
    if (e.code === 'Backspace' && !this.state.searchText.length) {
      this.removeLastTag()
    }

    this.refreshSuggestions(value)
  }

  catchEnter (e: KeyboardEvent) {
    const target = e?.target
    if (!isInputEl(target)) {
      return
    }

    if (this.props.canEndUserAddPublicTags && e.code === 'Enter' && target.value.length > 0) {
      e.stopPropagation()
      if (this.isTagSelected(target.value)) {
        return
      }
      this.selectPublicTag({ name: target.value })
      this.refreshSuggestions('')
    }
  }

  refreshSuggestions (searchText: string) {
    this.state.index.searchForFacetValues({ facetName: 'tags', facetQuery: searchText, facetingAfterDistinct: true }).then(({ facetHits }: { facetHits: FacetHit[] }) => {
      this.setState({ publicTagList: facetHits, searchText: searchText })
      this.tagInput && this.tagInput.focus()
    })
  }

  isTagSelected (tag: string) {
    return this.state.selectedTagsSet.has(tag.toLowerCase())
  }

  selectPublicTag (tag: Tag, remainFocusOnInput: boolean = true) {
    const { selectedPublicTags, selectedTagsSet } = this.state

    if (this.isTagSelected(tag.name)) {
      return
    }

    selectedTagsSet.add(tag.name.toLowerCase())
    selectedPublicTags.push(tag)

    this.setState({
      selectedPublicTags: selectedPublicTags,
      selectedTagsSet: selectedTagsSet,
      searchText: '',
      inputInFocus: false
    })

    if (this.props.onChange) {
      const tagsList = selectedPublicTags.map(tag => { return tag.name })
      this.props.onChange(tagsList)
    }

    if (remainFocusOnInput) {
      this.refreshSuggestions('')
    }
  }

  removeLastTag () {
    const { selectedPublicTags } = this.state

    if (!selectedPublicTags.length) {
      return
    }

    const lastSelected = selectedPublicTags[selectedPublicTags.length - 1]
    this.removePublicTag(lastSelected)
    this.refreshSuggestions('')
  }

  removePublicTag (tag: Tag) {
    const { selectedPublicTags, selectedTagsSet } = this.state

    if (!this.isTagSelected(tag.name)) {
      return
    }

    selectedTagsSet.delete(tag.name.toLowerCase())
    const index = selectedPublicTags.indexOf(tag)
    selectedPublicTags.splice(index, 1)

    if (this.props.onChange) {
      const tagsList = selectedPublicTags.map(tag => { return tag.name })
      this.props.onChange(tagsList)
    }

    this.setState({ selectedPublicTags: selectedPublicTags, selectedTagsSet: selectedTagsSet })
  }

  render ({ isEdit, inputName, inputId, canEndUserAddPublicTags, iconSize }: Props, { inputInFocus, publicTagList, selectedPublicTags, searchText, index }: State) {
    const tags = publicTagList.filter(tag => {
      return !this.isTagSelected(tag.value)
    })
    const closeIconSize = iconSize ?? 16
    return (
      <div className='public-tag-container qa-public-tag-container'>
        <div className={`public-tag-wrapper ${isEdit ? 'public-tag-edit-wrapper' : ''}`}>
          {isEdit
            ? (selectedPublicTags.length ? (
              selectedPublicTags.map(tag => (
                <input type='hidden' name={inputName} value={tag.name} />
              ))) : null)
            : <input type='hidden' name={inputName} id={inputId} value={selectedPublicTags.map(t => t.name).join(',')} />
          }
          {
            selectedPublicTags.length ? (selectedPublicTags.map(selected => {
              return (
                <div className='public-tag_selected-tag' onClick={() => this.removePublicTag(selected)}>
                  <div className='public-tag_selected-tag-name'>{selected.name}</div>
                  <div
                    className='public-tag_selected-remove-button'
                  >
                    <Icon icon='close' width={closeIconSize} height={closeIconSize} color={'currentColor'} />
                  </div>
                </div>
              )
            })) : null
          }
          <div className='public-tag-input-wrapper'>
            <input title=''
              ref={input => { this.tagInput = input }}
              class='form-control'
              placeholder='Add tags'
              type='text'
              onFocus={() => this.setState({ inputInFocus: true })}
              onBlur={() => this.setState({ inputInFocus: false })}
              onKeyUp={e => this.updateSearchText(e)}
              onKeyDown={e => this.catchEnter(e)}
            />
          </div>
          {
            inputInFocus && <div class='public-tag-list-container' ref={element => { this.dropDown = element }}>
              <DropDown tags={tags} selectPublicTag={this.selectPublicTag} searchText={searchText}
                canEndUserAddPublicTags={canEndUserAddPublicTags} isTagSelected={this.isTagSelected} />
            </div>
          }
        </div>
        { common.isModuleActive('publicTagAutoSuggestion') &&
          <AutoSuggestTags index={index} isTagSelected={this.isTagSelected} selectPublicTag={this.selectPublicTag} /> }
      </div>
    )
  }
}

const DropDown = ({ tags, selectPublicTag, canEndUserAddPublicTags, isTagSelected, searchText }: {
  tags: FacetHit[],
  searchText: string,
  selectPublicTag: (tag: Tag) => void,
  isTagSelected: (tag: string) => boolean,
  canEndUserAddPublicTags: boolean
}) => {
  let options = [];
  if (tags.length > 0) {
    options.push(...tags.map(tag => (
      <li className='public-tag-list-item' onMouseDown={
        () => selectPublicTag({ name: tag.value })
      }>
        <span dangerouslySetInnerHTML={{ __html: tag.highlighted }} />
        <span className='usage-count'>{tag.count}</span>
      </li>
    )))
  } else if (!canEndUserAddPublicTags || !isTagSelected(searchText)) {
    options.push(<li className='public-tag_not-found'>No results found</li>)
  }

  return <ul>
    { canEndUserAddPublicTags && <AddNewPublicTag searchText={searchText} selectPublicTag={selectPublicTag} isTagSelected={isTagSelected} /> }
    { options }
  </ul>
}

const AddNewPublicTag = ({ searchText, selectPublicTag, isTagSelected }: {
  searchText: string,
  selectPublicTag: (tag: Tag) => void,
  isTagSelected: (tag: string) => boolean
}) => {
  if (searchText.length === 0) {
    return null
  }

  if (isTagSelected(searchText)) {
    return <li className='public-tag-list-item existing-tag'>You have already added this tag</li>
  }

  return <li className='public-tag-list-item new-tag' onMouseDown={
    () => selectPublicTag({ name: searchText })
  }>Add new public tag ‘{searchText}’</li>
}

export { Tag }
